import { faGrid2, faUser } from "@fortawesome/pro-light-svg-icons";
import { faGrid2 as faGrid2Solid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, IconButton, Typography } from "@mui/material";
import { memo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useSelector } from "react-redux";

import type { Asset } from "@/Dashboard/pages/AssetLibrary/types";
import AccessControl from "@/components/AccessControl/AccessControl";
import { MEDIA_SIZE, PROPERTY_TIERS, TAB_STATUSES } from "@/constants";
import { useUrlParams } from "@/hooks";
import { CircleChecked, CircleUnchecked, ExpMenu } from "@/icons";
import {
  addCurrentSelectedItems,
  resetCurrentSelectedItems,
  setSelectAll,
  setSimpleValue,
  updateVisualBuilderData,
} from "@/store/actions";
import { useThunkDispatch } from "@/store/hooks";
import {
  currentMediaItemSelector,
  currentPropertySelector,
  currentSelectedItemsSelector,
  visualBuilderDataSelector,
} from "@/store/selectors";
import { getMediaLink } from "@/utils/mediaLink";

import Elements from "../Elements/Elements";
import ContactDialog from "../Header/ContactDialog";
import ContactDialogV2 from "../Header/ContactDialogV2";

const styles = {
  container: {
    alignItems: "center",
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    flex: "none",
    height: "56px",
    position: "relative",
    width: "100%",
    "& button": {
      pointerEvents: "auto",
      zIndex: 7001,
    },
  },
  bigButton: {
    position: "absolute",
    left: "16px",
  },
  smallButton: {
    background: "black",
    width: "40px",
    height: "40px",
  },
  innerMenu: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    flexDirection: "row",
    gap: "16px",
    height: "100%",
    marginLeft: isMobile ? "40px" : "240px",
    padding: "0px 16px",
  },
  allElements: {
    display: "flex",
    flexDirection: "row-reverse",
    height: "100%",
    left: 0,
    position: "absolute",
    right: 0,
    width: "100%",
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
};

interface ExperienceToolbarProps {
  readonly allElementData: unknown[];
  readonly filteredMediaItems: Asset[];
  readonly gridView: boolean;
  readonly leftDrawer: boolean;
  readonly leftDrawerAutoHide: boolean;
  readonly locked: boolean;
}

/**
 * Toolbar of an experience.
 * Has title, drawer control, digital bussiness card and 'select all' in grid view.
 */
function ExperienceToolbar(props: ExperienceToolbarProps) {
  const { locked, gridView, leftDrawer, leftDrawerAutoHide, filteredMediaItems, allElementData } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const dispatch = useThunkDispatch();

  const { mediaItem: currentMediaItem } = useSelector(currentMediaItemSelector);
  const { property } = useSelector(currentPropertySelector);
  const { selectAll } = useSelector(currentSelectedItemsSelector);
  const params = useUrlParams();
  const { visualBuilderData } = useSelector(visualBuilderDataSelector(params));

  const { createdBy = {}, flags } = visualBuilderData;

  const photo = getMediaLink({ size: MEDIA_SIZE.tiny, link: createdBy?.photoUrl });

  const { tabStatus } = params;
  const { tier } = property;
  const tierTitle = tier?.title;
  const { name: propertyName } = property;
  const experienceAvatars = property?.branding?.customSettings?.experienceAvatars || true;
  const tierAccessFlag = flags?.["tiers-phase-three"];

  const showUserIcon =
    tabStatus &&
    !(
      [
        TAB_STATUSES.HOMEBASE,
        TAB_STATUSES.COLLECT,
        TAB_STATUSES.CUSTOMIZE,
        TAB_STATUSES.PUBLIC_MEDIA_SHARE,
        TAB_STATUSES.PUBLIC_SINGLE_SHARE,
      ] as string[]
    ).includes(tabStatus) &&
    tier?.title &&
    [PROPERTY_TIERS.PRO, PROPERTY_TIERS.STANDARD].includes(
      tierTitle as typeof PROPERTY_TIERS.PRO | typeof PROPERTY_TIERS.STANDARD,
    ) &&
    experienceAvatars;

  const elementsPaddingRight = showUserIcon ? "65px" : "0px";

  /**
   * Opens/closes left drawer.
   */
  const toggleDrawerOpen = () => {
    if (locked && tabStatus === TAB_STATUSES.CUSTOMIZE) return;
    void dispatch(updateVisualBuilderData(params, { leftDrawer: !leftDrawer }));
  };

  /**
   * Toggles between grid view and normal view.
   */
  const toggleGridView = () => {
    if (locked && tabStatus === TAB_STATUSES.CUSTOMIZE) return;

    void dispatch(
      updateVisualBuilderData(params, {
        gridView: !gridView,
        currentMediaItemId: gridView ? filteredMediaItems[0]?.id : null,
      }),
    );
  };

  /**
   * Handles the 'select all' button.
   */
  const toggleSelectAll = () => {
    if (selectAll) {
      dispatch(resetCurrentSelectedItems());
    } else {
      dispatch(addCurrentSelectedItems(filteredMediaItems.reduce((a, v) => ({ ...a, [v.id]: v }), {})));
    }
    dispatch(setSelectAll(!selectAll));
  };

  /**
   * Handles user icon click.
   */
  const handleUserClick = () => {
    dispatch(setSimpleValue("contact_salesperson", true));
  };

  return (
    <Box sx={[styles.container, tabStatus === TAB_STATUSES.HOMEBASE && { marginTop: "40px" }]}>
      {!leftDrawerAutoHide && (
        <IconButton
          data-testid="drawer-button"
          aria-label="open drawer"
          color="inherit"
          edge="start"
          onClick={toggleDrawerOpen}
          size="large"
          sx={styles.bigButton}
          data-analytics="vb-experience-menu-toggle"
        >
          <ExpMenu />
        </IconButton>
      )}

      <Box sx={styles.innerMenu}>
        <IconButton data-testid="grid-view-button" sx={styles.smallButton} onClick={toggleGridView} size="large">
          <FontAwesomeIcon icon={gridView ? faGrid2Solid : faGrid2} color="white" style={{ fontSize: "16px" }} />
        </IconButton>

        {gridView &&
        tabStatus &&
        !([TAB_STATUSES.PUBLIC_SINGLE_SHARE, TAB_STATUSES.PUBLIC] as string[]).includes(tabStatus) ? (
          <AccessControl
            allowedPermissions={["experiences:create"]}
            allowedTiers={tierAccessFlag ? [PROPERTY_TIERS.PRO] : []}
          >
            <Box sx={{ flex: 1 }}>
              <Box
                data-testid="select-all-button"
                onClick={locked ? undefined : toggleSelectAll}
                component="span"
                sx={{ display: "flex", cursor: "pointer", gap: "5px" }}
              >
                {selectAll ? <CircleChecked /> : <CircleUnchecked />}
                <Typography>Select All</Typography>
              </Box>
            </Box>
          </AccessControl>
        ) : (
          <Box sx={styles.titleContainer}>
            <Typography variant="body1" sx={{ fontSize: "14px", fontWeight: "bold" }}>
              {propertyName}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "14px" }}>
              {currentMediaItem.name}
            </Typography>
          </Box>
        )}

        {!gridView && (
          <Box sx={[styles.allElements, { paddingRight: elementsPaddingRight }]}>
            <Elements anchorEl={anchorEl} setAnchorEl={setAnchorEl} elements={allElementData} />
          </Box>
        )}

        {showUserIcon && (
          <IconButton data-testid="user-button" onClick={handleUserClick} size="large" sx={styles.smallButton}>
            {photo ? (
              <Avatar src={photo} />
            ) : (
              <FontAwesomeIcon icon={faUser} color="white" style={{ fontSize: "16px" }} />
            )}
          </IconButton>
        )}
      </Box>

      {flags?.["digital-business-card"] ? (
        <ContactDialogV2 documentName="salesperson" createdBy={createdBy} />
      ) : (
        <ContactDialog documentName="salesperson" createdBy={createdBy} />
      )}
    </Box>
  );
}

export default memo(ExperienceToolbar);
